import {
  WSElement,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction
} from "@wingspanhq/fe-component-library";
import { FrequencyAndScheduleStatus } from "@wingspanhq/payments/dist/interfaces";
import { useMemo } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { INVOICING_PATH } from "../..";
import { KEYBOARD_SHORTCUT_SEARCH } from "../../../../constants/keyboardShortcuts";
import { AccountExpand } from "@wingspanhq/wingspan-api-sdk";
import { queriesAccounts } from "@query";
import { useSession } from "@hooks/useSession";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { isCountryUS } from "../../../../shared/selectors/selectorIsDomesticMember";
import pluralize from "pluralize";
import { useRedirectToByKeyPress } from "../../../../utils/useRedirectToByKeyPress";
import { PATH_PLATFORM_BALANCE } from "../../../PlatformBalance/paths";
import { AlertEnableTaxWithholdings } from "../../../TaxWithholdings/components/AlertEnableTaxWithholdings";
import { useCustomization } from "../../../customization";
import { DashboardTabs } from "../../components/DashboardTabs";
import { useQueryInvoiceTemplateRowsSummary } from "../../queries/useQueryInvoiceTemplateRowsSummary";
import { RouteDeposited } from "./routes/RouteDeposited";
import { RouteDrafts } from "./routes/RouteDrafts";
import { RouteInProgress } from "./routes/RouteInProgress";
import { RouteIndex } from "./routes/RouteIndex";
import { RouteOutstanding } from "./routes/RouteOutstanding";

export const RouteDashboard: React.FC = () => {
  const { terminology } = useCustomization();

  useBrowserPageTitle("Invoicing");
  const history = useHistory();
  const { accountId } = useSession();
  const featureFlags = useFeatureFlags();
  const queryInvoiceTemplateRowsSummary = useQueryInvoiceTemplateRowsSummary({
    filter: {
      status: {
        in: [
          FrequencyAndScheduleStatus.Active,
          FrequencyAndScheduleStatus.Draft
        ]
      }
    }
  });
  const queryAccount = queriesAccounts.getAccount.useQuery(
    { accountId, expand: [AccountExpand.COMPLIANCE_ENTITY] },
    { enabled: !!accountId && !!featureFlags.data?.platformBalance }
  );

  const showManageSeries = useMemo(
    () => !!queryInvoiceTemplateRowsSummary.data?.listSize,
    [queryInvoiceTemplateRowsSummary.data?.listSize]
  );

  useRedirectToByKeyPress(KEYBOARD_SHORTCUT_SEARCH, `${INVOICING_PATH}/search`);

  const pageActions = useMemo(() => {
    const actions: WSPageToolbarAction[] = [
      {
        label: "Search",
        icon: "search",
        buttonKind: "Tertiary",
        onClick: () => history.push(`${INVOICING_PATH}/search`),
        hideButtonOn: false
      },
      {
        label: "Create invoice",
        icon: "plus",
        buttonKind: "Primary",
        onClick: () => history.push(INVOICING_PATH + "/create")
      }
    ];

    if (showManageSeries) {
      actions.unshift({
        buttonKind: "Tertiary",
        label: "Scheduled invoices",
        onClick: () => history.push(INVOICING_PATH + "/scheduled")
      });
    }

    actions.push({
      label: "Payouts (Beta)",
      onClick: () => history.push(INVOICING_PATH + "/payouts")
    });

    if (featureFlags.data?.platformBalance) {
      const isInternational = queryAccount.data
        ? !isCountryUS(queryAccount.data.complianceEntity?.address?.country)
        : false;

      if (isInternational) {
        actions.push({
          label: "Platform balance",
          onClick: () => history.push(PATH_PLATFORM_BALANCE)
        });
      }
    }

    return actions;
  }, [
    featureFlags.data?.platformBalance,
    history,
    queryAccount.data,
    showManageSeries
  ]);

  return (
    <WSPage>
      <WSPageToolbar
        title={pluralize.plural(terminology().getPaidInvoice)}
        actions={pageActions}
      />

      <WSList gap="2XL">
        <AlertEnableTaxWithholdings />

        <WSElement>
          <DashboardTabs mb="L" />
          <Switch>
            <Route path={INVOICING_PATH + "/drafts"} component={RouteDrafts} />
            <Route
              path={INVOICING_PATH + "/outstanding"}
              component={RouteOutstanding}
            />
            <Route
              path={INVOICING_PATH + "/in-progress"}
              component={RouteInProgress}
            />
            <Route
              path={INVOICING_PATH + "/deposited"}
              component={RouteDeposited}
            />
            <Route path={INVOICING_PATH} component={RouteIndex} />
          </Switch>
        </WSElement>
      </WSList>
    </WSPage>
  );
};
